/*
	State management helpers

	Utils for creating react-contexts with selectors and dispatchers.

	usage:
		const actionHandlers = {
			INCREMENT_COUNT: (state, action) => ({ ...state, count: state.count + action.count }),
		};
		export { Provider, useSelector, useDispatch } = createLocalContextWithActionHandlers(actionHandlers);
	or better named:
		const c = createLocalContextWithActionHandlers(actionHandlers);
		export const OrderModalProvider = c.Provider;
		export const useOrderModalSelector = c.useSelector;
		export const useOrderModalDispatch = c.useDispatch;
*/

import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';

interface ProviderProps<T> {
	readonly value: T;
	readonly children: ReactNode;
}

export type Action = { type: string; [key: string]: any };
type ActionHandlers<T> = { [key: string]: (state: T, action: Action) => T };
type Thunk<T> = (dispatch: Dispatch, state: T) => unknown;
type LocalDispatch<T> = (action: Action | Thunk<T>) => void;
export type Dispatch = LocalDispatch<any>;
type Selector<T, TSelected> = (state: T) => TSelected;

type Store<T> = {
	getState: () => T;
	subscribe: (listener: () => void) => () => void;
	dispatch: LocalDispatch<T>;
};

const isAction = (value: unknown): value is Action => !!(value as Action)?.type;

export function createLocalContextWithActionHandlers<T>(actionHandlers: ActionHandlers<T>) {
	const createStore = (initialState: T): Store<T> => {
		let state = initialState;
		const listeners = new Set<() => void>();

		const dispatch: LocalDispatch<T> = action => {
			if (typeof action === 'function') {
				const result = action(dispatch, state);
				if (isAction(result)) {
					dispatch(result);
				}
				return;
			}
			if (!isAction(action)) return;
			const handler = actionHandlers[action.type];
			const nextState = handler ? handler(state, action) : state;
			if (nextState !== state) {
				state = nextState;
				listeners.forEach(listener => listener());
			}
		};

		return {
			getState: () => state,
			subscribe: listener => {
				listeners.add(listener);
				return () => {
					listeners.delete(listener);
				};
			},
			dispatch
		};
	};

	const emptyState = {} as T;
	const StoreContext = createContext<Store<T>>({
		getState: () => emptyState,
		subscribe: () => () => {},
		dispatch: () => {}
	});

	function useSelector<TSelected = any>(selector: Selector<T, TSelected>): TSelected {
		const store = useContext(StoreContext);
		return useSyncExternalStoreWithSelector(store.subscribe, store.getState, store.getState, selector);
	}

	const useDispatch = () => useContext(StoreContext).dispatch;

	const Provider = ({ children, value }: ProviderProps<T>) => {
		const [store] = useState(() => createStore({ ...value }));
		return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
	};

	return {
		Provider,
		useSelector,
		useDispatch
	};
}
